import apiClient from './client';

export interface Document {
  id: string;
  userId: string;
  tenantId: string;
  filename: string;
  originalFilename: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number;
  s3Key: string;
  s3Bucket: string;
  bedrockStatus: 'pending' | 'uploaded' | 'indexing' | 'indexed' | 'failed';
  bedrockIndexedAt?: string;
  bedrockError?: string;
  description?: string;
  tags?: string[];
  // Topic modeling fields
  mainTopic?: string;        // Primary topic/category (1 keyword)
  subTopics?: string[];      // Sub-topics (3-10 keywords)
  topics?: string[];         // Combined array for backward compatibility
  metadata?: Record<string, any>;
  chunkCount?: number;
  // Image extraction fields
  imageExtractionStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  imageCount?: number;
  imageExtractionError?: string;
  hasExtractableImages?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentListResponse {
  documents: Document[];
  total: number;
  limit: number;
  offset: number;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  documentId: string;
  s3Key: string;
  expiresIn: number;
}

export interface ListDocumentsParams {
  limit?: number;
  offset?: number;
  status?: string;
  fileType?: string;
  search?: string;
  sortBy?: 'createdAt' | 'filename' | 'fileSizeBytes';
  sortOrder?: 'asc' | 'desc';
}

export interface KBStatus {
  configured: boolean;
  bedrockKbId?: string;
  documentCount: number;
  indexedCount: number;
  lastSyncAt?: string;
}

class DocumentsService {
  async listDocuments(params: ListDocumentsParams = {}): Promise<DocumentListResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.fileType) queryParams.append('fileType', params.fileType);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await apiClient.get(`/documents?${queryParams.toString()}`);
    return response.data.data;
  }

  async getDocument(id: string): Promise<Document> {
    const response = await apiClient.get(`/documents/${id}`);
    return response.data.data;
  }

  async getUploadUrl(
    filename: string,
    contentType: string,
    fileSizeBytes: number
  ): Promise<UploadUrlResponse> {
    const response = await apiClient.post('/documents/upload-url', {
      filename,
      contentType,
      fileSizeBytes,
    });
    return response.data.data;
  }

  async uploadFile(uploadUrl: string, file: File, contentType?: string): Promise<void> {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType || file.type || 'application/octet-stream',
      },
    });
  }

  async confirmUpload(
    documentId: string,
    description?: string,
    tags?: string[]
  ): Promise<Document> {
    const response = await apiClient.post(`/documents/${documentId}/confirm`, {
      description,
      tags,
    });
    return response.data.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await apiClient.delete(`/documents/${id}`);
  }

  async deleteAllDocuments(deleteAllTenant = false): Promise<{ deletedCount: number }> {
    const params = deleteAllTenant ? '?all=true' : '';
    const response = await apiClient.delete(`/documents${params}`);
    return response.data.data;
  }

  /**
   * Download document using presigned S3 URL
   * Gets a short-lived presigned URL from API and triggers download
   */
  async downloadDocument(id: string, filename: string): Promise<void> {
    // Get presigned URL from API (requires authentication)
    const downloadUrl = await this.getDownloadUrl(id);

    // Fetch the file from S3 using presigned URL
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get presigned download URL (short-lived, 1 hour expiry)
   * The URL is authenticated on the API side before being generated
   */
  async getDownloadUrl(id: string): Promise<string> {
    const response = await apiClient.get(`/documents/${id}/download`);
    return response.data.data.downloadUrl;
  }

  async embedDocument(id: string, textContent: string): Promise<{ chunks: number }> {
    const response = await apiClient.post(`/documents/${id}/embed`, {
      textContent,
    });
    return response.data.data;
  }

  async getKBStatus(): Promise<KBStatus> {
    const response = await apiClient.get('/knowledge-base/status');
    return response.data.data;
  }

  async syncKnowledgeBase(): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post('/knowledge-base/sync');
    return response.data.data;
  }

  /**
   * Re-index a document (retry indexing or force re-process)
   * Triggers text extraction, topic extraction, and embedding creation
   * Uses force=true to re-index already completed documents
   */
  async reprocessDocument(id: string): Promise<{ success: boolean; chunks?: number; error?: string }> {
    const response = await apiClient.post(`/documents/${id}/process?force=true`);
    return response.data;
  }

  /**
   * Extract images from a PDF document for multimodal RAG
   * Uses Claude Vision to analyze pages with images and generates embeddings
   */
  async extractDocumentImages(id: string): Promise<{
    success: boolean;
    imagesProcessed: number;
    pageCount?: number;
    pagesWithImages?: number;
    error?: string;
  }> {
    const response = await apiClient.post(`/documents/${id}/extract-images`);
    return response.data;
  }

  /**
   * Get extracted images for a document
   */
  async getDocumentImages(id: string): Promise<{
    documentId: string;
    extractionStatus: string;
    imageCount: number;
    images: Array<{
      id: string;
      pageNumber: number;
      imageIndex: number;
      s3Key: string;
      caption?: string;
      ocrText?: string;
      embeddingStatus: string;
      width?: number;
      height?: number;
      format?: string;
      imageType?: string;
    }>;
  }> {
    const response = await apiClient.get(`/documents/${id}/images`);
    return response.data;
  }

  /**
   * Get presigned URL for an extracted image
   */
  async getImageUrl(documentId: string, s3Key: string): Promise<string> {
    const response = await apiClient.get(`/documents/${documentId}/images/url`, {
      params: { key: s3Key }
    });
    return response.data.url;
  }

  /**
   * Query images by similarity using text or image input
   */
  async queryImageSimilarity(params: {
    text?: string;
    image?: string;
    contentType?: string;
    documentId?: string;
    limit?: number;
  }): Promise<{
    success: boolean;
    query: { type: 'text' | 'image'; text?: string };
    results: Array<{
      image: {
        imageId: string;
        documentId: string;
        pageNumber: number;
        s3Key: string;
        caption?: string;
        similarity: number;
      };
      textChunks: Array<{
        chunkId: string;
        text: string;
        similarity: number;
      }>;
    }>;
  }> {
    const response = await apiClient.post('/query/image', params);
    return response.data;
  }
}

export const documentsService = new DocumentsService();
